import React, { useState, useContext, Fragment, useEffect, useLayoutEffect } from 'react';
import { map, indexOf, filter, find } from 'lodash';
import { MarkupContext, FocusBaseContext, MarkupListContext } from '../../../../contexts';
import { updateRegionRequest } from "../../../../requests";
import Respondent from './respondent';

const MarkupsList = () => {
  const {
    markupTool, onMarkupSettingsChange, onReloadMarkupsRequest,
    currentSummary, saveSettings, heatmap, selectedRegionId
  } = useContext(FocusBaseContext);
  const { deselectComments } = useContext(MarkupContext);

  const [ hideAll, setHideAll ] = useState(false);
  useEffect(() => {
    setHideAll(false);
  }, [ heatmap ]);

  const hasHighlighted = (markupTool.anyHighlighted);
  const hasVisibleComments = (markupTool.anyCommentVisible());
  const hasSelectedComments = (markupTool.hasSelectedComments());
  const groupedSelectedComments = (markupTool.groupedSelectedComments);
  const groupedComments = (markupTool.groupedComments);

  const respondentClasses = (area, isSelected) => (markupTool.respondentClasses(area, isSelected));
  const getColor = (reaction) => (currentSummary.colors[reaction]);
  const demosTitle = (demo) => (demo[0]);
  const demosText = (demo) => (demo[1]);
  const respondentIsHidden = (respondentId) => (markupTool.isRespondentHidden(respondentId));
  const toggleHighlight = (respondent, isSelected) => {
    markupTool.toggleHighlight(respondent, isSelected);
    onMarkupSettingsChange();
  };
  const hintMessageEye = (respondentId) => (markupTool.hintMessage(respondentId));
  const hintMessageMarkupType = (kind) => (markupTool.hintMarkupKind(kind === 'area'));
  const toggleAllRespondents = (visible) => {
    markupTool.toggleAllRespondents(visible);
    onMarkupSettingsChange();
  };
  const toggleRespondent = (respondentId) => {
    markupTool.toggleRespondent(respondentId);
    saveSettings(
      {
        respondent_id: respondentId,
        respondent_hidden: markupTool.isRespondentHidden(respondentId)
      }
    );
    onMarkupSettingsChange();
  };
  const toggleMarkupVisible = (area) => {
    markupTool.toggleMarkupVisible(area.id);
    saveSettings(
      {
        report_comment_id: area.id,
        markup_visible: area.markup_visible
      }
    );
    onMarkupSettingsChange();
  };

  const handleClearHighlight = () => {
    toggleHighlight();
  };
  const handleToggleAllRespondents = () => {
    toggleAllRespondents(hideAll);
    setHideAll(!hideAll);
  };
  const handleDeselectComments = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    deselectComments();
  };

  const handleMoveHighlights = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Down' || event.key === 'Up') {
      markupTool.moveHighlight(event.key === 'ArrowUp' || event.key === 'Up');
      event.preventDefault();

      onMarkupSettingsChange({ updateScrolls: true });
    }
  };

  useLayoutEffect(() => {
    if (onReloadMarkupsRequest?.updateScrolls) {
      const selectedNode = document.querySelector('.summary-markup_comment.-selected');
      const delta = (
        selectedNode.offsetTop -
        document.querySelector('.summary-markup_comments').offsetTop
      );

      const rowHeight = selectedNode.getBoundingClientRect().height;
      const windowHeight = document.querySelector('.summary-markup_comments').getBoundingClientRect().height;
      const scrollPos = document.querySelector('.summary-markup_comments').scrollTop;
      const commentHeaderHeight = (
        document.querySelector('.summary-markup_comment.-header').getBoundingClientRect().height
      );

      const childNodes = selectedNode.parentNode.childNodes;
      const firstEl = indexOf(childNodes, selectedNode) === 1;
      const lastEl = indexOf(childNodes, selectedNode) === childNodes.length - 1;

      if (delta + rowHeight > scrollPos + windowHeight) {
        document.querySelector('.summary-markup_comments').scrollTop = (
          delta - windowHeight + 1.5 * rowHeight + (lastEl ? commentHeaderHeight : 0)
        );
      }

      if (delta < scrollPos) {
        document.querySelector('.summary-markup_comments').scrollTop = (
          delta - 0.5 * rowHeight - (firstEl ? commentHeaderHeight : 0)
        );
      }
    }
  }, [ onReloadMarkupsRequest ]);

  const markupListValue = {
    demosTitle, demosText, toggleRespondent, hintMessageEye, respondentIsHidden,
    respondentClasses, getColor, toggleHighlight, hintMessageMarkupType, toggleMarkupVisible
  };

  return (
    <MarkupListContext.Provider value={ markupListValue }>
      <div className="form-group -lg2">
        <div className="summary-markup_comment -global">
          {
            hasHighlighted &&
            <span
              className="summary-markup_comment_action -clear-highlight"
              title="Clear Highlight"
              role="button"
              onClick={ handleClearHighlight }
            >
              Clear Highlight
            </span>
          }
          {
            hasVisibleComments &&
            <span
              className="summary-markup_comment_action icon-link -no-text -toggle"
              title="Toggle All"
              role="button"
              onClick={ handleToggleAllRespondents }
            >
              {
                hideAll ? (
                  <svg className="icon-link_icon icon -eye-closed">
                    <use xlinkHref="#svg_eye-closed" />
                  </svg>
                ) : (
                  <svg className="icon-link_icon icon -eye-open"><use xlinkHref="#svg_eye-open" /></svg>
                )
              }
              <span className="icon-link_text">Toggle All</span>
            </span>
          }
        </div>
        <div className="summary-markup_comments" tabIndex="0" onKeyDown={ handleMoveHighlights }>
          {
            hasSelectedComments &&
            <div className="summary-markup_comment -group">
              <div className="summary-markup_comment_title">Selected</div>
              <span
                className="summary-markup_comment_action icon-link -no-text"
                title="Deselect"
                role="button"
                onClick={ handleDeselectComments }
              >
                <svg className="icon-link_icon icon -i-remove-round">
                  <use xlinkHref="#svg_i-remove-round" />
                </svg>
              </span>
            </div>
          }
          {
            map(groupedSelectedComments, (areas, respondentId) => (
              <Fragment key={ `selected-${respondentId}` }>
                <Respondent respondentId={ respondentId } areas={ areas } selected />
              </Fragment>
            ))
          }
          {
            hasSelectedComments &&
            <div className="summary-markup_comment -group">
              <div className="summary-markup_comment_title">Others</div>
            </div>
          }
          {
            map(groupedComments, (areas, respondentId) => (
              <Fragment key={ `selected-${respondentId}` }>
                <Respondent respondentId={ respondentId } areas={ areas } />
              </Fragment>
            ))
          }
        </div>
      </div>
    </MarkupListContext.Provider>
  );
};

export default MarkupsList;
